/**
 * Simulation eines gefma-Mitgliederportals: die geltenden Richtlinien als PDF und Word zum
 * Herunterladen — erzeugt aus der Richtlinien-Werkstatt (Blockmodell, ohne Entwürfe).
 *
 * Versteckt: kein Menüeintrag, nur über die Adresse (/gefma-richtlinien). Hinter dem Login des
 * Portals, aber ohne dessen Oberfläche, damit die Seite wie ein eigenes Portal aussieht. Sichtbar
 * ist, was der Nutzer auch in der Werkstatt lesen darf; der Export prüft das auf dem Server.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { authenticationAtom } from "../State";
import { useGuidelinesAPI, type ExportFormat, type GuidelineSummary } from "./GuidelinesAPI";
import { saveBlob } from "./ExportMenu";

/** „GEFMA190.2023-06.pdf“ → Nummer und Ausgabe; alles andere (Probekopien) bleibt draußen. */
const parse = (title: string) => {
  const m = /^GEFMA\s*(\d+(?:-\d+)?)[.\s]+(\d{4}-\d{2})/i.exec(title.replace(/\.(pdf|docx?)$/i, ""));
  return m ? { number: m[1], edition: m[2] } : null;
};

/** Titel von der Titelseite der Richtlinie (im Dokument nicht als Feld geführt). */
const TITLES: Record<string, { title: string; subtitle?: string }> = {
  "190": { title: "Betreiberverantwortung 2.0 im Facility Management", subtitle: "inkl. ESG" },
  "163-1": { title: "ESG im Facility Management", subtitle: "gefma-Definition" },
  "198-1": { title: "Dokumentation im Facility Management", subtitle: "Begriffsabgrenzung, Vorgehensweise, Gliederung und Instrumente" },
};

const FORMATS: { format: ExportFormat; label: string }[] = [
  { format: "pdf", label: "PDF" },
  { format: "docx", label: "Word" },
];

type Entry = GuidelineSummary & { number: string; editionLabel: string };

export default function GefmaPortalPage() {
  const { t } = useTranslation();
  const [authentication] = useAtom(authenticationAtom);
  const customer = authentication?.customer ?? "";
  const api = useGuidelinesAPI(customer);
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!customer) return;
    api
      .listGuidelines()
      .then((items) =>
        setEntries(
          items
            .filter((g) => !g.superseded)
            .flatMap((g) => {
              const p = parse(g.title);
              return p ? [{ ...g, number: p.number, editionLabel: p.edition }] : [];
            })
            .sort((a, b) => a.number.localeCompare(b.number, "de", { numeric: true }))
        )
      )
      .catch((e) => {
        toast.error(e instanceof Error ? e.message : String(e));
        setEntries([]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const download = async (entry: Entry, format: ExportFormat) => {
    const key = `${entry._id}:${format}`;
    setBusy(key);
    try {
      const { blob, name } = await api.exportDocument(entry._id, format);
      saveBlob(blob, name);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f6fa] text-[#1b2433]">
      <header className="bg-[#1f3f99] text-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-4">
          <div className="flex items-baseline gap-3">
            <span className="text-2xl font-bold lowercase tracking-tight">gefma</span>
            <span className="hidden text-sm text-white/80 sm:inline">{t("gefmaPortal.tagline")}</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="rounded border border-white/40 px-2 py-0.5 text-xs uppercase tracking-wide">{t("gefmaPortal.simulation")}</span>
            <span className="hidden text-white/80 md:inline">{authentication?.email}</span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-10">
        <h1 className="text-3xl font-semibold text-[#1f3f99]">{t("gefmaPortal.title")}</h1>
        <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-[#46526a]">{t("gefmaPortal.intro")}</p>

        <section className="mt-8 overflow-hidden rounded-md border border-[#d9dfeb] bg-white">
          {entries === null ? (
            <div className="flex items-center gap-2 p-6 text-sm text-[#46526a]">
              <Loader2 size={16} className="animate-spin" /> {t("gefmaPortal.loading")}
            </div>
          ) : entries.length === 0 ? (
            <div className="p-6 text-sm text-[#46526a]">{t("gefmaPortal.empty")}</div>
          ) : (
            <ul className="divide-y divide-[#e6eaf2]">
              {entries.map((entry) => {
                const known = TITLES[entry.number];
                return (
                  <li key={entry._id} className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center" data-guideline={entry.number}>
                    <div className="flex w-24 shrink-0 flex-col">
                      <span className="text-xs uppercase tracking-wide text-[#6b7690]">GEFMA</span>
                      <span className="text-xl font-semibold tabular-nums text-[#1f3f99]">{entry.number}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{known?.title ?? entry.title}</div>
                      {known?.subtitle && <div className="text-sm text-[#46526a]">{known.subtitle}</div>}
                      <div className="mt-1 text-xs text-[#6b7690]">
                        {t("gefmaPortal.edition", { edition: entry.editionLabel })}
                        {entry.workingGroup?.name ? ` · ${entry.workingGroup.name}` : ""}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      {FORMATS.map(({ format, label }) => {
                        const running = busy === `${entry._id}:${format}`;
                        return (
                          <button
                            key={format}
                            type="button"
                            disabled={!!busy}
                            onClick={() => download(entry, format)}
                            data-download={format}
                            className="inline-flex items-center gap-1.5 rounded border border-[#1f3f99] px-3 py-1.5 text-sm font-medium text-[#1f3f99] transition-colors hover:bg-[#1f3f99] hover:text-white disabled:opacity-50"
                          >
                            {running ? <Loader2 size={14} className="animate-spin" /> : format === "pdf" ? <FileText size={14} /> : <Download size={14} />}
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <p className="mt-6 text-xs text-[#6b7690]">{t("gefmaPortal.footnote")}</p>
      </main>
    </div>
  );
}
